import { useCallback, useRef, useState } from 'react';
import { Room, RoomEvent, ConnectionState, DataPacket_Kind, RoomOptions } from 'livekit-client';
import * as Sentry from '@sentry/browser';

interface NetworkMetrics {
  bandwidth: number;
  latency: number;
  packetLoss: number;
  jitter: number;
  score: number;
}

interface HardwareMetrics {
  cpu: 'high' | 'medium' | 'low';
  memory: number;
  browser: string;
  webrtcSupport: boolean;
  score: number;
}

interface DeviceAvailability {
  camera: boolean;
  microphone: boolean;
  speaker: boolean;
}

interface DataChannelTestResult {
  reliable: boolean;
  lossy: boolean;
  bidirectional: boolean;
  latency: number;
}

export interface PreflightTestResult {
  overall: 'pass' | 'warning' | 'fail';
  network: NetworkMetrics;
  hardware: HardwareMetrics;
  devices: DeviceAvailability;
  dataChannel: DataChannelTestResult;
  recommendations: string[];
  canProceed: boolean;
  timestamp: number;
}

interface TestProgress {
  stage: 'idle' | 'devices' | 'network' | 'connection' | 'dataChannel' | 'complete' | 'error';
  progress: number;
  message: string;
}

export const usePreflightTest = () => {
  const [testProgress, setTestProgress] = useState<TestProgress>({
    stage: 'idle',
    progress: 0,
    message: ''
  });
  const [testResult, setTestResult] = useState<PreflightTestResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const testRoomRef = useRef<Room | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const updateProgress = (stage: TestProgress['stage'], progress: number, message: string) => {
    setTestProgress({ stage, progress, message });
    console.log(`[PreflightTest] ${stage}: ${message} (${progress}%)`);
  };

  const checkDevices = async (): Promise<DeviceAvailability> => {
    updateProgress('devices', 10, 'Checking camera and microphone...');

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasCamera = devices.some(d => d.kind === 'videoinput');
      const hasMicrophone = devices.some(d => d.kind === 'audioinput');
      const hasSpeaker = devices.some(d => d.kind === 'audiooutput');

      // Try to get actual media streams to verify permissions
      if (hasCamera && hasMicrophone) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });
          // Clean up stream
          stream.getTracks().forEach(track => track.stop());
          updateProgress('devices', 20, 'Camera and microphone verified');
        } catch (error) {
          console.warn('[PreflightTest] Media permission denied:', error);
        }
      }

      return {
        camera: hasCamera,
        microphone: hasMicrophone,
        speaker: hasSpeaker
      };
    } catch (error) {
      console.error('[PreflightTest] Device check failed:', error);
      return {
        camera: false,
        microphone: false,
        speaker: false
      };
    }
  };

  const checkHardware = async (): Promise<HardwareMetrics> => {
    updateProgress('devices', 25, 'Checking system capabilities...');

    // Check browser and WebRTC support
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    const browserVersion = isChrome ?
      parseInt(userAgent.match(/Chrome\/(\d+)/)?.[1] || '0') : 0;

    // Check memory (if available)
    const memory = (performance as any).memory?.jsHeapSizeLimit
      ? Math.round((performance as any).memory.jsHeapSizeLimit / (1024 * 1024 * 1024))
      : 4; // Default to 4GB if not available

    // Simple CPU check based on hardware concurrency
    const cores = navigator.hardwareConcurrency || 2;
    const cpu: HardwareMetrics['cpu'] =
      cores >= 8 ? 'high' :
      cores >= 4 ? 'medium' : 'low';

    // Check WebRTC support
    const webrtcSupport = !!(
      window.RTCPeerConnection &&
      window.RTCSessionDescription &&
      window.RTCIceCandidate &&
      navigator.mediaDevices?.getUserMedia
    );

    // Calculate score
    let score = 0;
    if (cpu === 'high') score += 40;
    else if (cpu === 'medium') score += 30;
    else score += 10;

    if (memory >= 4) score += 30;
    else if (memory >= 2) score += 20;
    else score += 10;

    if (webrtcSupport) score += 20;
    if (isChrome && browserVersion >= 90) score += 10;

    return {
      cpu,
      memory,
      browser: isChrome ? `Chrome ${browserVersion}` : userAgent.split(' ')[0],
      webrtcSupport,
      score
    };
  };

  const checkNetwork = async (serverUrl: string, token: string): Promise<NetworkMetrics> => {
    updateProgress('network', 35, 'Testing network speed...');

    const startTime = Date.now();
    let latency = 0;
    let bandwidth = 0;
    let packetLoss = 0;
    let jitter = 0;

    try {
      // Test latency with multiple pings
      const pingResults: number[] = [];
      for (let i = 0; i < 3; i++) {
        const pingStart = Date.now();
        await fetch(serverUrl.replace('wss://', 'https://').replace('/ws', '/health'), {
          method: 'HEAD',
          mode: 'no-cors'
        }).catch(() => null);
        pingResults.push(Date.now() - pingStart);
      }
      latency = Math.round(pingResults.reduce((a, b) => a + b, 0) / pingResults.length);

      // Calculate jitter (variation in latency)
      const avgPing = pingResults.reduce((a, b) => a + b, 0) / pingResults.length;
      jitter = Math.round(
        Math.sqrt(pingResults.reduce((sum, ping) => sum + Math.pow(ping - avgPing, 2), 0) / pingResults.length)
      );

      updateProgress('network', 45, 'Measuring bandwidth...');

      // Estimate bandwidth by downloading a small payload
      const testSize = 100 * 1024; // 100KB test
      const downloadStart = Date.now();
      const testData = new ArrayBuffer(testSize);
      const downloadTime = Date.now() - downloadStart;
      bandwidth = Math.round((testSize * 8) / (downloadTime / 1000) / 1024 / 1024); // Mbps

      // Estimate packet loss (simplified - would need actual WebRTC stats for accuracy)
      packetLoss = jitter > 50 ? 5 : jitter > 30 ? 2 : 0;

    } catch (error) {
      console.error('[PreflightTest] Network check failed:', error);
    }

    // Calculate network score
    let score = 0;
    if (latency < 100) score += 30;
    else if (latency < 200) score += 20;
    else if (latency < 300) score += 10;

    if (bandwidth > 2) score += 30;
    else if (bandwidth > 1) score += 20;
    else if (bandwidth > 0.5) score += 10;

    if (packetLoss === 0) score += 20;
    else if (packetLoss < 2) score += 10;

    if (jitter < 30) score += 20;
    else if (jitter < 50) score += 10;

    return {
      bandwidth: bandwidth || 1, // Default to 1 Mbps if measurement failed
      latency: latency || 100,
      packetLoss,
      jitter,
      score
    };
  };

  const testDataChannels = async (room: Room): Promise<DataChannelTestResult> => {
    updateProgress('dataChannel', 70, 'Testing data channels...');

    const result: DataChannelTestResult = {
      reliable: false,
      lossy: false,
      bidirectional: false,
      latency: 0
    };

    try {
      // Wait for room to be fully connected
      if (room.state !== ConnectionState.Connected) {
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Connection timeout')), 5000);
          const handleConnected = () => {
            clearTimeout(timeout);
            room.off(RoomEvent.Connected, handleConnected);
            resolve();
          };
          room.on(RoomEvent.Connected, handleConnected);
        });
      }

      updateProgress('dataChannel', 75, 'Sending test messages...');

      // Test reliable channel
      const reliableTestData = new TextEncoder().encode('test_reliable_' + Date.now());
      const reliableStart = Date.now();

      try {
        await room.localParticipant.publishData(reliableTestData, { reliable: true });
        result.reliable = true;
        result.latency = Date.now() - reliableStart;
      } catch (error) {
        console.error('[PreflightTest] Reliable channel test failed:', error);
      }

      // Test lossy channel
      const lossyTestData = new TextEncoder().encode('test_lossy_' + Date.now());
      try {
        await room.localParticipant.publishData(lossyTestData, { reliable: false });
        result.lossy = true;
      } catch (error) {
        console.error('[PreflightTest] Lossy channel test failed:', error);
      }

      // Test bidirectional communication (simplified - would need echo from server)
      result.bidirectional = result.reliable && result.lossy;

      updateProgress('dataChannel', 85, 'Data channels verified');
    } catch (error) {
      console.error('[PreflightTest] DataChannel test failed:', error);
      Sentry.captureException(error, {
        tags: { component: 'PreflightTest', test: 'dataChannel' }
      });
    }

    return result;
  };

  const testConnection = async (serverUrl: string, token: string): Promise<{
    success: boolean;
    dataChannel: DataChannelTestResult;
  }> => {
    updateProgress('connection', 55, 'Establishing test connection...');

    const room = new Room({
      // Minimal options for testing
      adaptiveStream: false,
      dynacast: false,
      stopLocalTrackOnUnpublish: false,
      reconnectPolicy: {
        maxRetries: 2,
        nextRetryDelayInMs: () => 1000
      },
      // Ensure DataChannels are enabled
      publishDefaults: {
        stopMicTrackOnMute: false,
      }
    } as RoomOptions);

    testRoomRef.current = room;

    try {
      // Set connection timeout
      const connectionTimeout = setTimeout(() => {
        throw new Error('Connection timeout');
      }, 15000);

      await room.connect(serverUrl, token);
      clearTimeout(connectionTimeout);

      updateProgress('connection', 65, 'Connection established');

      // Test data channels
      const dataChannelResult = await testDataChannels(room);

      return {
        success: true,
        dataChannel: dataChannelResult
      };
    } catch (error) {
      console.error('[PreflightTest] Connection test failed:', error);
      Sentry.captureException(error, {
        tags: { component: 'PreflightTest', test: 'connection' }
      });

      return {
        success: false,
        dataChannel: {
          reliable: false,
          lossy: false,
          bidirectional: false,
          latency: 0
        }
      };
    } finally {
      // Clean up
      if (room.state !== ConnectionState.Disconnected) {
        await room.disconnect();
      }
      testRoomRef.current = null;
    }
  };

  const generateRecommendations = (
    network: NetworkMetrics,
    hardware: HardwareMetrics,
    devices: DeviceAvailability,
    dataChannel: DataChannelTestResult
  ): string[] => {
    const recommendations: string[] = [];

    // Network recommendations
    if (network.latency > 200) {
      recommendations.push('High network latency detected. Consider using a wired connection.');
    }
    if (network.bandwidth < 1) {
      recommendations.push('Low bandwidth detected. Close other applications using internet.');
    }
    if (network.packetLoss > 2) {
      recommendations.push('Packet loss detected. Check your network connection stability.');
    }
    if (network.jitter > 50) {
      recommendations.push('Network instability detected. Avoid using WiFi if possible.');
    }

    // Hardware recommendations
    if (hardware.cpu === 'low') {
      recommendations.push('Low CPU performance detected. Close unnecessary applications.');
    }
    if (hardware.memory < 2) {
      recommendations.push('Low memory available. Close browser tabs and applications.');
    }
    if (!hardware.webrtcSupport) {
      recommendations.push('WebRTC not fully supported. Update your browser.');
    }
    if (!hardware.browser.includes('Chrome')) {
      recommendations.push('For best experience, use Google Chrome browser.');
    }

    // Device recommendations
    if (!devices.camera) {
      recommendations.push('No camera detected. Check camera connection and permissions.');
    }
    if (!devices.microphone) {
      recommendations.push('No microphone detected. Check microphone connection and permissions.');
    }

    // DataChannel recommendations
    if (!dataChannel.reliable || !dataChannel.lossy) {
      recommendations.push('Data channel issues detected. This may cause connection instability.');
    }
    if (dataChannel.latency > 1000) {
      recommendations.push('High data channel latency. Connection may be unstable.');
    }

    return recommendations;
  };

  const runPreflightTest = useCallback(async (serverUrl: string, token: string): Promise<PreflightTestResult> => {
    if (isRunning) {
      throw new Error('Test already running');
    }

    setIsRunning(true);
    abortControllerRef.current = new AbortController();

    try {
      updateProgress('devices', 0, 'Starting pre-flight test...');

      // Step 1: Check devices
      const devices = await checkDevices();

      // Step 2: Check hardware
      const hardware = await checkHardware();

      // Step 3: Check network
      const network = await checkNetwork(serverUrl, token);

      // Step 4: Test connection and data channels
      const { success: connectionSuccess, dataChannel } = await testConnection(serverUrl, token);

      // Generate recommendations
      const recommendations = generateRecommendations(network, hardware, devices, dataChannel);

      // Calculate overall score
      const totalScore = (network.score + hardware.score) / 2;
      const hasDataChannelIssues = !dataChannel.reliable || !dataChannel.lossy;

      let overall: PreflightTestResult['overall'] = 'fail';
      let canProceed = false;

      if (totalScore >= 70 && connectionSuccess && !hasDataChannelIssues) {
        overall = 'pass';
        canProceed = true;
      } else if (totalScore >= 50 && connectionSuccess) {
        overall = 'warning';
        canProceed = true; // Allow with warning
      } else {
        overall = 'fail';
        canProceed = false;
      }

      const result: PreflightTestResult = {
        overall,
        network,
        hardware,
        devices,
        dataChannel,
        recommendations,
        canProceed,
        timestamp: Date.now()
      };

      setTestResult(result);
      updateProgress('complete', 100, 'Pre-flight test complete');

      // Log to Sentry for monitoring
      Sentry.addBreadcrumb({
        category: 'preflight',
        message: 'Pre-flight test completed',
        level: overall === 'fail' ? 'error' : overall === 'warning' ? 'warning' : 'info',
        data: result
      });

      return result;
    } catch (error) {
      console.error('[PreflightTest] Test failed:', error);
      updateProgress('error', 0, 'Pre-flight test failed');

      Sentry.captureException(error, {
        tags: { component: 'PreflightTest' }
      });

      // Return fail result on error
      const failResult: PreflightTestResult = {
        overall: 'fail',
        network: { bandwidth: 0, latency: 999, packetLoss: 100, jitter: 999, score: 0 },
        hardware: { cpu: 'low', memory: 0, browser: 'Unknown', webrtcSupport: false, score: 0 },
        devices: { camera: false, microphone: false, speaker: false },
        dataChannel: { reliable: false, lossy: false, bidirectional: false, latency: 0 },
        recommendations: ['Pre-flight test failed. Please check your connection and try again.'],
        canProceed: false,
        timestamp: Date.now()
      };

      setTestResult(failResult);
      return failResult;
    } finally {
      setIsRunning(false);
      abortControllerRef.current = null;
    }
  }, [isRunning]);

  const cancelTest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (testRoomRef.current && testRoomRef.current.state !== ConnectionState.Disconnected) {
      testRoomRef.current.disconnect();
    }
    setIsRunning(false);
    updateProgress('idle', 0, '');
  }, []);

  const resetTest = useCallback(() => {
    setTestResult(null);
    setTestProgress({
      stage: 'idle',
      progress: 0,
      message: ''
    });
  }, []);

  return {
    runPreflightTest,
    cancelTest,
    resetTest,
    testProgress,
    testResult,
    isRunning
  };
};